import React, {useState, useEffect, useRef} from 'react'
import { Button, Col, Row } from 'antd';
import dayjs from 'dayjs'
import { enableDaysLimit, daysLimit } from '../../util/details';

const DatePickerMobileLog = ({
    showDatePicker, 
    setShowDatePicker, 
    StyledDiv,
    actionDetails,
    setActionDetails,
    setLogValidationErrors,
}) => {
    const daysRef = useRef(null);
    const monthsRef = useRef(null);
    const yearsRef = useRef(null);

    const getCurrentYear = () => {
        const date = new Date();
        return date.getFullYear();
    };

    const year = getCurrentYear();
    const [months, setMonths] = React.useState([]);
    const [days, setDays] = React.useState([]);
    // CHANGE: Renamed this state to a generic name since it may not always be 60 days.
    // Holds the computed buckets of months and days based on the current range mode.
    const [dateBucketsData, setDateBucketsData] = React.useState({});
    const [selectedData, setSelectedData] = useState({
        day: 0,
        month: 0,
        year: 0
    });
    const [error, setError] = useState(false);
    
    // CHANGE: New helper replaces the hard-coded "next 60 days" generator.
    // When enableLimit=true, builds months/days for the next `limitDays` days (default 60).
    // When enableLimit=false, builds months/days for the remainder of the current year (full months).
    const buildDateBuckets = ({ enableLimit, limitDays }) => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const start = new Date(today);

        const monthsOut = [];
        const daysOut = {};

        const ensureContainers = (y, m) => {
            if (!daysOut[y]) daysOut[y] = {};
            if (!daysOut[y][m]) daysOut[y][m] = [];
        };

        if (enableLimit) {
            const end = new Date(today);
            end.setDate(today.getDate() + (typeof limitDays === 'number' ? limitDays : 60));

            let cursor = new Date(start);
            while (cursor <= end) {
                const m = cursor.getMonth();
                const d = cursor.getDate();
                const y = cursor.getFullYear();

                if (!monthsOut.some(mm => mm.month === m && mm.year === y)) {
                    monthsOut.push({ month: m, year: y });
                }

                ensureContainers(y, m);
                daysOut[y][m].push(d);

                cursor.setDate(cursor.getDate() + 1);
            }
        } else {
            // CHANGE: Unlimited mode now includes the remainder of the current year AND the full next year.
            // Why: Allow selecting across current/next year. Additionally, for the current month,
            // only include days from today onward to avoid past dates in the current year/month.
            const startMonth = start.getMonth();
            const todayDay = start.getDate();
            const nextYear = currentYear + 1;

            // Current year: from current month to December
            for (let m = startMonth; m <= 11; m++) {
                monthsOut.push({ month: m, year: currentYear });
                ensureContainers(currentYear, m);
                const lastDay = new Date(currentYear, m + 1, 0).getDate();
                // CHANGE: If this is the current month in the current year, start from todayDay; else start from 1
                const startDayForMonth = (m === startMonth) ? todayDay : 1;
                for (let d = startDayForMonth; d <= lastDay; d++) {
                    daysOut[currentYear][m].push(d);
                }
            }

            // Next year: full January to December
            for (let m = 0; m <= 11; m++) {
                monthsOut.push({ month: m, year: nextYear });
                ensureContainers(nextYear, m);
                const lastDay = new Date(nextYear, m + 1, 0).getDate();
                for (let d = 1; d <= lastDay; d++) {
                    daysOut[nextYear][m].push(d);
                }
            }
        }

        return { months: monthsOut, days: daysOut };
    };

    useEffect(() => {
        let mounted = true;

        if (mounted) {
            // CHANGE: Build date buckets based on the new toggleable range settings.
            const buckets = buildDateBuckets({ enableLimit: enableDaysLimit, limitDays: daysLimit });
            setDateBucketsData(buckets);

            // CHANGE: Determine initial selected year (due date year or current year)
            const initialYear = actionDetails.dueDate ? parseDateString(actionDetails.dueDate).year : year;
            // CHANGE: Filter months to only those in the selected year for display
            const monthsForYear = (buckets.months || []).filter(m => m.year === initialYear);
            setMonths(monthsForYear);

            const selectedMonth = actionDetails.dueDate ? parseDateString(actionDetails.dueDate).month : (monthsForYear[0] ? monthsForYear[0].month : -1);
            const selectedYear = initialYear;

            const daysInMonth = getDaysInMonth(selectedYear, selectedMonth, buckets.days);
            setDays(daysInMonth);
            // CHANGE: Default month now set to the first available month within the selected year.
            const formDate = actionDetails.dueDate ? parseDateString(actionDetails.dueDate) : { day: -1, month: selectedMonth, year: selectedYear };
            setSelectedData(formDate);

            centerSelectedItem(daysRef, formDate.day);
            centerSelectedItem(monthsRef, formDate.month);
            // CHANGE: Center the selected year as well for consistent UX.
            centerSelectedItem(yearsRef, formDate.year);
        }

        return () => {  
            mounted = false;
        }
    // CHANGE: Added enableDaysLimit and daysLimit so UI updates if these controls change.
    }, [showDatePicker, actionDetails.dueDate, year, enableDaysLimit, daysLimit]);

    useEffect(() => {
        let mounted = true;

        if (mounted) {
            centerSelectedItem(daysRef, selectedData.day);
            centerSelectedItem(monthsRef, selectedData.month);
        }

        return () => {  
            mounted = false;
        }
    }, [selectedData]);

    const parseDateString = (dateString) => {
        const [year, month, day] = dateString.split('-').map(Number);
        return { day, month: month - 1, year: year };
    };

    const getDaysInMonth = (year, month, days) => {
        // CHANGE: Defensive checks in case buckets are empty due to settings or edge dates.
        if (!days || !days[year] || !days[year][month]) {
            return [];
        }
        return days[year][month];
    };


    // CHANGE: Removed legacy getNextSixtyDays(). New buildDateBuckets() covers both limited and unlimited modes.

    const getMonthName = (monthIndex) => {
        const monthNames = [
          "January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December"
        ];
        return monthNames[monthIndex];
    };

    const centerSelectedItem = (ref, selectedIndex) => {
        if (ref.current) {
            const selectedItem = ref.current.querySelector(`[data-index="${selectedIndex}"]`);
            if (selectedItem) {
                const container = ref.current;
                const containerHeight = container.clientHeight;
                const itemHeight = selectedItem.clientHeight;
                const itemOffsetTop = selectedItem.offsetTop;

                const scrollTo = itemOffsetTop - (containerHeight / 2) + (itemHeight / 2);
                container.scrollTop = scrollTo;
            }
        }
    };
    

    const handleSetDate = () => {
        const { day, month, year } = selectedData;
        let dueDate = '';
    
        dueDate += `${year}`;
    
        if (month < 10) {
            if (month === 9)
                dueDate += `-10`;
            else
                dueDate += `-0${month + 1}`;
        } else {
            dueDate += `-${month + 1}`;
        }
    
        if (day < 10) {
            dueDate += `-0${day}`;
        } else {
            dueDate += `-${day}`;
        }
    
        
        const isValidDate = validateDateFormat(day, month, year);
        if (!isValidDate) {
            setError(true);
            return;
        }
    
        setActionDetails({ ...actionDetails, dueDate: dueDate, defaultDueDate: dayjs(dueDate) });
        setLogValidationErrors(prevState => ({ ...prevState, dueDate: "" }));
        setShowDatePicker(false);
    };

    const validateDateFormat = (day, month, year) => {
        const selectedDate = new Date(year, month, day);
    
        if (isNaN(selectedDate.getTime())) {
            return false;
        }
    
        return (
            selectedDate.getFullYear() === year &&
            selectedDate.getMonth() === month &&
            selectedDate.getDate() === day
        );
    };
    

  return (
    <>
        {showDatePicker && <div className="modal-overlay"
                style={{
                    height: "100dvh",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "end",
                    backgroundColor: "transparent"
                }}
            >
                <Col 
                    xl={{span: 24}}
                    lg={{span: 24}}
                    md={{span: 24}}
                    sm={{span: 24}}
                    xs={{span: 24}} 
                    style={{
                        height: "55dvh",
                    }}

                >
                    <div className="modal"
                        style={{
                            height: "100%"
                        }}
                    >
                        <Row
                            style={{
                                marginBottom: "10px",
                                height: "15%",
                            }}
                        >
                            <Col
                                span={8}
                                style={{
                                    display: "flex",
                                    justifyContent: "start",
                                }}
                            >
                                <Button
                                    onClick={() => {
                                        handleSetDate()
                                    }}
                                >Select</Button>
                            </Col>

                            <Col
                                span={8}
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "start",
                                    color: "red"
                                }}
                            >
                                {error && <StyledDiv>Invalid Date</StyledDiv>}
                            </Col>
                            

                            <Col
                                span={8}
                                style={{
                                    display: "flex",
                                    justifyContent: "end",
                                }}
                            >
                                <Button
                                    onClick={() => {
                                        setShowDatePicker(false)
                                        setError(false)
                                    }}
                                >Close</Button>
                            </Col>
                        </Row>

                        <Row
                            style={{
                                marginBottom: "10px",
                                height: "10%",
                            }}
                        >
                            <Col
                                span={8}
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                }}
                            >
                                <StyledDiv
                                    style={{
                                        fontWeight: "600"
                                    }}
                                >Day</StyledDiv>
                            </Col>

                            <Col
                                span={8}
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                }}
                            >
                                <StyledDiv
                                    style={{
                                        fontWeight: "600"
                                    }}
                                >Month</StyledDiv>
                            </Col>

                            <Col
                                span={8}
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                }}
                            >
                                <StyledDiv
                                    style={{
                                        fontWeight: "600"
                                    }}
                                >Year</StyledDiv>
                            </Col>
                        </Row>
                        <hr/>

                        <Row
                            style={{
                                height: "70%",
                            }}
                        >
                            <Col
                                span={8}
                                className="scroll-container"
                                ref={daysRef}
                            >
                                {days.map((day, index) => {
                                    return(
                                        <Row
                                            key={day + index}
                                            data-index={day}
                                            style={{
                                                display: "flex",
                                                justifyContent: "center",
                                                marginBottom: "20px",
                                                paddingTop: index === 0 ? "75px" : "0px",
                                                paddingBottom: index === days.length - 1 ? "70px" : "0px",
                                                
                                                
                                            }}
                                        >
                                            <StyledDiv
                                                onClick={() => {
                                                    setSelectedData({...selectedData, day: day});
                                                    if (error) {
                                                        setError(false);
                                                    }
                                                }}
                                                style={{
                                                    fontSize: "16px",
                                                    color: selectedData.day === day ? 'rgb(174,32,114)' : 'black',
                                                    cursor: "pointer",
                                                }}
                                            >{day}</StyledDiv>
                                        </Row>
                                    )
                                })}
                            </Col>

                            <Col
                                span={8}
                                className="scroll-container"
                                ref={monthsRef}
                            >
                                {months.map((month, index) => {
                                    return(
                                        <Row
                                            key={month.month + index}
                                            data-index={month.month}
                                            style={{
                                                display: "flex",
                                                justifyContent: "center",
                                                marginBottom: "10px",
                                                paddingTop: index === 0 ? "75px" : "0px",
                                                paddingBottom: index === months.length - 1 ? "70px" : "0px",
                                                
                                            }}
                                        >
                                            <StyledDiv
                                                onClick={() => {
                                                    if (error) {
                                                        setError(false);
                                                    }
                                                    
                                                    const daysInMonth = getDaysInMonth(month.year, month.month, dateBucketsData.days);
                                                    let day = selectedData.day;

                                                    
                                                    if (Array.isArray(daysInMonth) && day > daysInMonth.length) {
                                                        day = -1;
                                                    }

                                                    setDays(daysInMonth);
                                                    
                                                    setSelectedData({...selectedData, day: -1, days: daysInMonth, month: month.month, year: month.year});
                                                    
                                                    
                                                }}
                                                style={{
                                                    fontSize: "16px",
                                                    color: selectedData.month === month.month ? 'rgb(174,32,114)' : 'black',
                                                    cursor: "pointer",
                                                }}
                                            >{getMonthName(month.month)}</StyledDiv>
                                        </Row>
                                    )
                                })}
                            </Col>

                            <Col
                                span={8}
                                className="scroll-container"
                                ref={yearsRef}
                            >
                                {/* CHANGE: Year selection column. Shows current year and next year.
                                    Why: Allow users to pick year explicitly and filter months/days accordingly. */}
                                {[year, year + 1].map((yr, index) => {
                                    return (
                                        <Row
                                            key={yr}
                                            data-index={yr}
                                            style={{
                                                display: "flex",
                                                justifyContent: "center",
                                                marginBottom: "10px",
                                                paddingTop: index === 0 ? "75px" : "0px",
                                                paddingBottom: index === 1 ? "70px" : "0px",
                                            }}
                                        >
                                            <StyledDiv
                                                onClick={() => {
                                                    // CHANGE: Update selected year and filter months to this year.
                                                    const monthsForYear = (dateBucketsData.months || []).filter(m => m.year === yr);
                                                    setMonths(monthsForYear);

                                                    // Choose current selected month if available in this year, otherwise first month in that year
                                                    const monthExistsInYear = monthsForYear.some(m => m.month === selectedData.month);
                                                    const nextMonth = monthExistsInYear ? selectedData.month : (monthsForYear[0] ? monthsForYear[0].month : -1);

                                                    const daysInMonth = getDaysInMonth(yr, nextMonth, dateBucketsData.days);
                                                    setDays(daysInMonth);

                                                    setSelectedData({ ...selectedData, year: yr, month: nextMonth, day: -1 });

                                                    if (error) setError(false);
                                                }}
                                                style={{
                                                    fontSize: "16px",
                                                    color: selectedData.year === yr ? 'rgb(174,32,114)' : 'black',
                                                    cursor: "pointer",
                                                }}
                                            >{yr}</StyledDiv>
                                        </Row>
                                    );
                                })}
                            </Col>
                        </Row>

                        <Row
                            style={{
                                marginBottom: "10px",
                                height: "15%",
                            }}
                        >
                            
                        </Row>
                        
                    </div> 
                </Col> 
            </div>} 
    </>
  )
}

export default DatePickerMobileLog
